import BigNumber from "bignumber.js"
import { LineSeriesOption } from "echarts"
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Card, Form, OverlayTrigger, Popover, Tooltip } from "react-bootstrap"
import { average as findAverage } from "simple-statistics"
import { calcMarkLineOffset } from "../getSpeedChartOptionsHelpers"
import { calcOverUnderPerformance } from "../helpers/calculateSensitivity"
import { getPortStaysData } from "../helpers/getPortStaysData"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { SegmentSensitivityData } from "../hooks/usePrevetGetSensitivityDataForSegment"
import { useFetchVesselSensitivityInput } from "../hooks/useFetchVesselSensitivityInput"
import { CreateVesselSensitivityInputObject } from "../hooks/usePrevetAPI"
import { RootState } from "store"
import { useSelector } from "react-redux"
import { Button, Spinner } from "react-bootstrap"
import { useAddVesselSensitivityInput } from "../hooks/useAddVesselSensitivityInput"
import { toast } from "react-toastify"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"
import { VesselSegmentInputSelection } from "./PrevetMain"
import Badge from "react-bootstrap/Badge"

interface PrevetPerformanceExposureCardProps {
  describedLadenSpeed: string
  ladenFuelCon: string
  ladenSeaDays: string
  describedBallastSpeed: string
  ballastFuelCon: string
  ballastSeaDays: string
  segmentSensitivityData: SegmentSensitivityData | undefined
  ladenChartInstance: MutableRefObject<any>
  ballastChartInstance: MutableRefObject<any>
  speedGraphLadenAvgSpeed: number | undefined
  speedGraphBallastAvgSpeed: number | undefined
  portStaysLadenAvgSpeed: number | undefined
  portStaysBallastAvgSpeed: number | undefined
  realLadenMaxSpeed: number | undefined
  realBallastMaxSpeed: number | undefined
  vesselName: string
  defaultFuelCon: number
  setUserLadenVesselSpeed: (speed: string | undefined) => void
  setUserBallastVesselSpeed: (speed: string | undefined) => void
  setHirePrice: Dispatch<SetStateAction<string>>
  hirePrice: string
  setFuelPrice: React.Dispatch<React.SetStateAction<string>>
  fuelPrice: string
  selectedDate: string
  vesselImo: string
  chosenSegment: VesselSegmentInputSelection
  isShowSeaMarginChecked: boolean
  ladenAvgMargin: number | undefined
  ballastAvgMargin: number | undefined
  isDescribedSpeedChanged: boolean
}

export function PrevetPerformanceExposureCard({
  describedLadenSpeed,
  ladenFuelCon,
  ladenSeaDays,
  describedBallastSpeed,
  ballastFuelCon,
  ballastSeaDays,
  ladenChartInstance,
  ballastChartInstance,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  portStaysLadenAvgSpeed,
  portStaysBallastAvgSpeed,
  realLadenMaxSpeed,
  realBallastMaxSpeed,
  segmentSensitivityData,
  vesselName,
  defaultFuelCon,
  setUserLadenVesselSpeed,
  setUserBallastVesselSpeed,
  setHirePrice,
  hirePrice,
  setFuelPrice,
  fuelPrice,
  selectedDate,
  vesselImo,
  chosenSegment,
  isShowSeaMarginChecked,
  ladenAvgMargin,
  ballastAvgMargin,
  isDescribedSpeedChanged,
}: PrevetPerformanceExposureCardProps) {
  const { postTrackingEvent } = useWoopraAPI()
  const { setTabStateValue, getTabStateValue, refetchTrigger } =
    usePrevetTabStateContext()
  const ladenMarginOverride = getTabStateValue("ladenMarginOverride", undefined) as number | undefined
  const ballastMarginOverride = getTabStateValue("ballastMarginOverride", undefined) as number | undefined
  const isManualMarginOverride = ladenMarginOverride !== undefined || ballastMarginOverride !== undefined
  const standardVesselDefaultSpeed = 12

  const {
    vesselSensitivityInputData,
    fetchVesselSensitivityInput,
    getVesselSensitivityInputLoading,
  } = useFetchVesselSensitivityInput()

  const { createVesselSensitivityInput, isLoading } =
    useAddVesselSensitivityInput()

  const userInitial = useSelector(
    (state: RootState) => state.user.user?.name ?? ""
  )

  const chosenOrganisation = useSelector(
    (state: RootState) => state.user.chosenOrganization
  )
  //get vessel sensitivity input data
  useEffect(() => {
    fetchVesselSensitivityInput({
      orgId: chosenOrganisation?.id || "",
      vesselDateSelected: selectedDate,
      vesselImo: vesselImo,
    })
  }, [vesselImo, selectedDate, chosenOrganisation, refetchTrigger])

  //set vsi for every changes
  const currentVsi = useMemo<CreateVesselSensitivityInputObject>(
    () => ({
      orgId: chosenOrganisation?.id || "",
      vesselImo,
      vesselDateSelected: new Date(selectedDate).toISOString(),
      updatedByName: userInitial,
      data: {
        ballastKt: null,
        ballastMt: null,
        ballastSd: null,
        ladenKt: null,
        ladenMt: null,
        ladenSd: null,
        hirePrice: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      },
    }),
    [
      chosenOrganisation?.id,
      vesselImo,
      selectedDate,
      userInitial,
      hirePrice,
      fuelPrice,
    ]
  )

  //map hire and fuel price to vessel sensitivity input if not null
  useEffect(() => {
    if (
      vesselSensitivityInputData &&
      new Date(selectedDate).getTime() ===
        new Date(vesselSensitivityInputData.vesselDateSelected).getTime()
    ) {
      const timer = setTimeout(() => {
        setFuelPrice(
          vesselSensitivityInputData.data?.fuelPrice?.toString() ?? fuelPrice
        )
        setHirePrice(
          vesselSensitivityInputData.data?.hirePrice?.toString() ?? hirePrice
        )
      }, 1000) //Debounce to avoid rapid state update
      return () => clearTimeout(timer)
    }
  }, [vesselSensitivityInputData])

  // Keep track of minor customization to grey out the Std Vsl box
  const [isStandardVesselBoxGray, setIsStandardVesselBoxGray] = useState(false)

  // Ref to store previous values
  const previousStateValues = useRef({ hirePrice, fuelPrice })
  const spotIndexName =
    segmentSensitivityData?.benchmark.spot_index_name || "P5TC"

  const [performanceExposure, setPerformanceExposure] = useState<
    number | undefined
  >()

  const [vesselComparisonMessage, setVesselComparisonMessage] = useState(
    `Std vsl / ${vesselName}`
  )

  const [isSaveButtonHidden, setIsSaveButtonHidden] = useState<boolean>(true)

  useEffect(() => {
    if (segmentSensitivityData?.benchmark.spot_price == null) return
    const currentValue = (hirePrice || "").trim()
    if (currentValue !== String(segmentSensitivityData.benchmark.spot_price)) {
      const newValue =
        currentValue === "" || isNaN(Number(currentValue))
          ? String(segmentSensitivityData.benchmark.spot_price)
          : currentValue
      setHirePrice(String(newValue))
    } else {
      setHirePrice(String(segmentSensitivityData.benchmark.spot_price))
    }
  }, [segmentSensitivityData?.benchmark.spot_price])

  useEffect(() => {
    if (segmentSensitivityData?.benchmark.fuel_price == null) return
    const currentValue = (fuelPrice || "").trim()
    if (currentValue !== String(segmentSensitivityData.benchmark.fuel_price)) {
      const newValue =
        currentValue === "" || isNaN(Number(currentValue))
          ? String(segmentSensitivityData.benchmark.fuel_price)
          : currentValue
      setFuelPrice(String(newValue))
    } else {
      setFuelPrice(String(segmentSensitivityData.benchmark.fuel_price))
    }
  }, [segmentSensitivityData?.benchmark.fuel_price])

  useEffect(() => {
    const ladenAvgSpeedForCalculation = speedGraphLadenAvgSpeed
      ? speedGraphLadenAvgSpeed
      : portStaysLadenAvgSpeed
    const ballastAvgSpeedForCalculation = speedGraphBallastAvgSpeed
      ? speedGraphBallastAvgSpeed
      : portStaysBallastAvgSpeed

    if (!ladenAvgSpeedForCalculation && !ballastAvgSpeedForCalculation) {
      return
    }

    const performance = calcPerformance({
      describedLadenSpeed,
      describedBallastSpeed,
      speedGraphLadenAvgSpeed: ladenAvgSpeedForCalculation,
      speedGraphBallastAvgSpeed: ballastAvgSpeedForCalculation,
      ladenFuelCon,
      ballastFuelCon,
      hirePrice,
      fuelPrice,
      ladenSeaDays,
      ballastSeaDays,
      isShowSeaMarginChecked,
      ladenAvgMargin,
      ballastAvgMargin,
    })
    setPerformanceExposure(performance)
    setTabStateValue("perfExposureValue", performance, undefined)
  }, [
    describedLadenSpeed,
    describedBallastSpeed,
    portStaysLadenAvgSpeed,
    portStaysBallastAvgSpeed,
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    ladenFuelCon,
    ballastFuelCon,
    hirePrice,
    fuelPrice,
    ladenSeaDays,
    ballastSeaDays,
    isShowSeaMarginChecked,
    ladenAvgMargin,
    ballastAvgMargin,
  ])

  function updateChart({
    isLaden,
    speed,
    isReset,
  }: {
    isLaden: boolean
    speed: string
    isReset: boolean
  }) {
    if (
      (isLaden && !speedGraphLadenAvgSpeed) ||
      (!isLaden && !speedGraphBallastAvgSpeed)
    ) {
      return
    }
    const avgSpeed = isLaden
      ? speedGraphLadenAvgSpeed
      : speedGraphBallastAvgSpeed

    const chart = isLaden ? ladenChartInstance : ballastChartInstance
    const opts = chart.current.getOption()

    const maxYAxis = isLaden ? realLadenMaxSpeed : realBallastMaxSpeed

    const yAxis = !maxYAxis // Grow or shrink yAxis depending on user input
      ? [{ ...opts.yAxis[0] }]
      : [
          {
            ...opts.yAxis[0],
            max: [
              Number(speed) > maxYAxis // If user input TC desc > speed graph max
                ? Number(speed) + 1
                : BigNumber(maxYAxis + 1)
                    .decimalPlaces(0, BigNumber.ROUND_HALF_CEIL)
                    .toString(),
            ],
          },
        ]

    // When focusing any input, draw the usr vessel line on speed graph
    chart.current.setOption({
      ...opts,
      series: [
        {
          ...(opts.series as LineSeriesOption[])[0],
        },
        {
          ...(opts.series as LineSeriesOption[])[1],
        },
        {
          ...(opts.series as LineSeriesOption[])[2],
        },
        !isReset
          ? {
              name: `User Vsl`,
              markLine: {
                symbolSize: 7,
                lineStyle: {
                  width: 1,
                  opacity: 0.75,
                },
                data: [
                  {
                    name: "user vessel",
                    yAxis: Number(speed),
                    label: {
                      offset: calcMarkLineOffset({
                        label: Number(speed),
                        compared: avgSpeed as number,
                      }),
                      fontSize: 10,
                      formatter: (params: any) => {
                        return `${BigNumber(params.data.yAxis).toFormat(
                          1
                        )} User Vsl`
                      },
                    },
                  },
                ],
              },
            }
          : {
              name: "User Vsl", // Put this on its own markLine because of symbol: none
              type: "line",
              markLine: {
                lineStyle: {
                  width: 0,
                  opacity: 0,
                },
              },
            },
      ],
      yAxis,
    })
  }

  const updateVesselComparisonMessage = () => {
    setVesselComparisonMessage(`User vsl / ${vesselName}`)
  }

  const { extraTime, extraFuel } = useExtraCalculations(
    parseFloat(describedBallastSpeed),
    parseFloat(ballastFuelCon),
    parseFloat(describedLadenSpeed),
    parseFloat(ladenFuelCon),
    speedGraphLadenAvgSpeed ?? portStaysLadenAvgSpeed,
    speedGraphBallastAvgSpeed ?? portStaysBallastAvgSpeed,
    parseFloat(ballastSeaDays),
    parseFloat(ladenSeaDays),
    isShowSeaMarginChecked,
    ladenAvgMargin,
    ballastAvgMargin
  )

  //handle submit for vessel sensitivity input
  const handleAddVesselSensitivityInput = async (
    vsi: CreateVesselSensitivityInputObject
  ) => {
    try {
      if (
        vesselSensitivityInputData?.data.ballastKt &&
        vesselSensitivityInputData.data.ballastMt &&
        vesselSensitivityInputData.data.ballastSd &&
        vesselSensitivityInputData.data.ladenKt &&
        vesselSensitivityInputData.data.ladenMt &&
        vesselSensitivityInputData.data.ladenSd
      ) {
        vsi.data.ballastKt = vesselSensitivityInputData.data.ballastKt
        vsi.data.ballastMt = vesselSensitivityInputData.data.ballastMt
        vsi.data.ballastSd = vesselSensitivityInputData.data.ballastSd
        vsi.data.ladenKt = vesselSensitivityInputData.data.ladenKt
        vsi.data.ladenMt = vesselSensitivityInputData.data.ladenMt
        vsi.data.ladenSd = vesselSensitivityInputData.data.ladenSd
      }
      await createVesselSensitivityInput(vsi)
      const message = "Saved input for Performance Exposure"
      toast.success(message, {
        position: "bottom-right",
        autoClose: 8000,
      })

      //fetch again
      await fetchVesselSensitivityInput({
        orgId: vsi.orgId,
        vesselImo: vsi.vesselImo,
        vesselDateSelected: selectedDate,
      })
    } catch (err) {
      toast.error(
        "Failed to save hireprice and fuelprice for vessel sensitivity input",
        {
          position: "bottom-right",
          autoClose: 8000,
        }
      )
    }
  }

  const renderTooltip = (margin: number) => {
    return (
      <Popover id="popover-basic">
        <Popover.Body>
          <div className="text-muted">Sea Margin Impact: {(margin * 100).toFixed(1)}%</div>
          <div className="text-muted">
            Refer to Vessel Speed widget for more info.
          </div>
        </Popover.Body>
      </Popover>
    )
  }

  return (
    <Card className="PrevetContainer_PerformanceExposure w-100 h-100">
      <Card.Body>
        <div className="d-flex gap-2 align-items-start">
          <Card.Title>Performance Exposure</Card.Title>
          {isShowSeaMarginChecked && (
            <OverlayTrigger
              placement="bottom"
              overlay={renderTooltip(
                findAverage([ballastAvgMargin ?? 0, ladenAvgMargin ?? 0])
              )}
            >
              <Badge
                pill
                bg="primary"
                className="px-2 py-1 d-inline-flex justify-content-center align-items-center cursor-auto"
              >
                {isManualMarginOverride ? "Manual SM Adjusted" : "SM Adjusted"}
              </Badge>
            </OverlayTrigger>
          )}
        </div>
        <div className="text-muted">
          {vesselSensitivityInputData &&
          vesselSensitivityInputData?.data.ballastKt != null &&
          vesselSensitivityInputData.data.ballastMt !== null &&
          vesselSensitivityInputData.data.ballastSd !== null &&
          vesselSensitivityInputData?.data.ladenKt != null &&
          vesselSensitivityInputData.data.ladenMt !== null &&
          vesselSensitivityInputData.data.ladenSd !== null ? (
            <span>
              {
                isDescribedSpeedChanged ? "Inserted Circulated": "Saved Circulated "
              }  Vsl speed of {vesselName} is{" "}
              {computePerformanceDifference({
                speedGraphBallastAvgSpeed,
                speedGraphLadenAvgSpeed,
                describedBallastSpeed,
                describedLadenSpeed,
                isShowSeaMarginChecked,
                ladenAvgMargin,
                ballastAvgMargin,
              })}{" "}
              vs Pre-Vetting {isShowSeaMarginChecked ? "SM-adjusted" : ""} avg
              speed
            </span>
          ) : (
            <span>
              {
                isDescribedSpeedChanged ? "Inserted Circulated ": "Std "
              }  Vsl speed is{" "}
              {computePerformanceDifference({
                speedGraphBallastAvgSpeed,
                speedGraphLadenAvgSpeed,
                describedBallastSpeed,
                describedLadenSpeed,
                isShowSeaMarginChecked,
                ladenAvgMargin,
                ballastAvgMargin,
              })}{" "}
              vs Pre-Vetting {isShowSeaMarginChecked ? "SM-adjusted" : ""} avg
              speed
            </span>
          )}
        </div>
        {/* Main body */}
        {performanceExposure ? (
          <>
            <h1
              style={{
                color: `${performanceExposure > 0 ? "#e61f2f" : "#00763b"}`,
              }}
              className="mt-4 text-center"
            >
              {performanceExposure > 0
                ? performanceExposure.toLocaleString("en-US")
                : Math.abs(performanceExposure).toLocaleString("en-US")}{" "}
              <DownIcon
                direction={`${performanceExposure > 0 ? "down" : "up"}`}
              />
            </h1>
          </>
        ) : (
          <>
            <h1 className="mt-4 text-center text-muted">0</h1>
          </>
        )}
        <div className="my-1 w-75 mx-auto border border-muted" />
        <div className="d-flex w-100 justify-content-around">
          <div className="d-flex gap-1">
            {extraTime !== null &&
            performanceExposure &&
            performanceExposure > 0 ? (
              <>
                <div className="text-danger fs-4">{extraTime.toFixed(1)}d</div>
                <div className="text-muted fs-4">EXTRA Time</div>
              </>
            ) : (
              <>
                <div className="text-muted fs-4">0d</div>
                <div className="text-muted fs-4">EXTRA Time</div>
              </>
            )}
          </div>
          <div className="d-flex gap-1">
            {extraFuel !== null &&
            performanceExposure &&
            performanceExposure > 0 ? (
              <>
                <div className="text-danger fs-4">{extraFuel.toFixed(1)}mt</div>
                <div className="text-muted fs-4">EXTRA Fuel</div>
              </>
            ) : (
              <>
                <div className="text-muted fs-4">0mt</div>
                <div className="text-muted fs-4">EXTRA Fuel</div>
              </>
            )}
          </div>
        </div>
        {/* check if vessel sensitivity is empty */}
        {vesselSensitivityInputData?.data.ballastKt != null &&
        vesselSensitivityInputData.data.ballastMt !== null &&
        vesselSensitivityInputData.data.ballastSd !== null &&
        vesselSensitivityInputData?.data.ladenKt != null &&
        vesselSensitivityInputData.data.ladenMt !== null &&
        vesselSensitivityInputData.data.ladenSd !== null ? (
          <div className="d-flex flex-column justify-content-center gap-3 mt-5">
            <div className="w-100">
              <div className="text-muted text-center font-italic">
                {vesselSensitivityInputData.updatedByName}'s description for
                calculation
              </div>
              <div className="d-flex justify-content-center gap-3 flex-nowrap mt-3 align-items-center">
                <h3 style={{ color: "#ff5c29" }}>B</h3>
                <div>
                  <div style={{ color: "#ff5c29" }}>
                    {vesselSensitivityInputData.data.ballastKt}
                  </div>
                  <div style={{ color: "#ff5c29" }}>
                    {vesselSensitivityInputData.data.ballastMt}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-muted">kts</div>
                  <div className="text-muted">mt/day</div>
                </div>
                <div>
                  <div style={{ color: "#001446" }}>
                    {vesselSensitivityInputData.data.ladenKt}
                  </div>
                  <div style={{ color: "#001446" }}>
                    {vesselSensitivityInputData.data.ladenMt}
                  </div>
                </div>
                <h3 style={{ color: "#001446" }}>L</h3>
              </div>
            </div>
            {/* Lower part */}
            <div className="w-100 d-flex justify-content-between align-items-start gap-2">
              {/* Left part */}
              <div className="w-50">
                <table className="w-100 mt-3">
                  <tbody>
                    <tr>
                      <td className="text-muted">{spotIndexName}</td>
                      <td className="text-muted text-end">
                        $
                        {segmentSensitivityData?.benchmark.spot_price.toLocaleString(
                          "en-US"
                        )}{" "}
                        pdpr
                      </td>
                    </tr>
                    {/* <div className="my-" /> */}
                    <tr>
                      <td>
                        <div className="mt-2" />
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Fuel Price</td>
                      <td className="text-muted text-end">
                        $
                        {segmentSensitivityData?.benchmark.fuel_price.toLocaleString(
                          "en-US"
                        )}{" "}
                        mt/d
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Divider */}
              <div className="vr" />
              {/* Right part */}
              <div className="w-50 mt-2 d-flex flex-column gap-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">Hire</div>
                  <div className="position-relative w-50 me-2">
                    {getVesselSensitivityInputLoading ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        variant="light"
                      />
                    ) : (
                      <>
                        <div className="PrevetSensitivity_DollarSign">$</div>
                        <Form.Control
                          className="bg-light w-100 text-end"
                          aria-label="Amount (to the nearest dollar)"
                          value={
                            hirePrice === "" || isNaN(Number(hirePrice))
                              ? ""
                              : Number(hirePrice).toLocaleString("en-US")
                          }
                          onFocus={(e) => {
                            updateChart({
                              isLaden: true,
                              speed: describedLadenSpeed,
                              isReset: false,
                            })
                            updateChart({
                              isLaden: false,
                              speed: describedBallastSpeed,
                              isReset: false,
                            })
                            updateVesselComparisonMessage()
                            setIsStandardVesselBoxGray(true)
                            setUserLadenVesselSpeed(describedLadenSpeed)
                            setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                            setIsSaveButtonHidden(false)
                          }}
                          onChange={(e) => {
                            if (e.target.value === "") {
                              setHirePrice("")
                            }
                            const regex = new RegExp(
                              /^(?:\d{1,3}(?:[,]\d{1,4})*|\d+)$/
                            )
                            const match = regex.test(e.target.value)
                            if (!match) {
                              return
                            }
                            const num = convertToLocaleNumStringToNumber(
                              e.target.value
                            ) // Convert 15,333 to 15333
                            setHirePrice(String(num))
                            postTrackingEvent({
                              page: "/prevetting",
                              event: "click_hire_price",
                              properties: {
                                input: "hire_price",
                                component: "performance_exposure",
                                vesselSegment: chosenSegment.value,
                              },
                            })
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">Fuel</div>
                  <div className="position-relative w-50 me-2">
                    {getVesselSensitivityInputLoading ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        variant="light"
                      />
                    ) : (
                      <>
                        <div className="PrevetSensitivity_DollarSign">$</div>
                        <Form.Control
                          className="w-100 bg-light text-end"
                          aria-label="Amount (to the nearest dollar)"
                          value={fuelPrice}
                          onFocus={(e) => {
                            updateChart({
                              isLaden: true,
                              speed: describedLadenSpeed,
                              isReset: false,
                            })
                            updateChart({
                              isLaden: false,
                              speed: describedBallastSpeed,
                              isReset: false,
                            })
                            setUserLadenVesselSpeed(describedLadenSpeed)
                            setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                            updateVesselComparisonMessage()
                            setIsStandardVesselBoxGray(true)
                            setIsSaveButtonHidden(false)
                          }}
                          onChange={(e) => {
                            setFuelPrice(e.target.value)
                            postTrackingEvent({
                              page: "/prevetting",
                              event: "click_fuel_price",
                              properties: {
                                input: "fuel_price",
                                component: "performance_exposure",
                                vesselSegment: chosenSegment.value,
                              },
                            })
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  {isSaveButtonHidden ? null : (
                    <Button
                      variant="primary"
                      className="PrevetCards_SaveSensitivityButton d-flex align-items-center justify-content-center"
                      onClick={() =>
                        handleAddVesselSensitivityInput(
                          currentVsi as CreateVesselSensitivityInputObject
                        )
                      }
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          variant="light"
                        />
                      ) : (
                        <>
                          {" "}
                          <span className="ti-save font-weight-bold" />
                          <span className="font-weight-bold ms-2">Save</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-center gap-3 mt-5">
            <div className="w-100">
              <div className="text-muted text-center font-italic">Std Vsl</div>
              <table className="w-100 mt-3">
                <tbody>
                  <tr>
                    <td className="text-muted">Speed</td>
                    <td className="text-muted text-end">
                      {standardVesselDefaultSpeed} kts
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Fuel Cons</td>
                    <td className="text-muted text-end">
                      {defaultFuelCon} mt/d
                    </td>
                  </tr>
                  {/* Divider */}
                  <tr>
                    <td>
                      <div className="mt-4" />
                    </td>
                  </tr>

                  <tr>
                    <td className="text-muted">{spotIndexName}</td>
                    <td className="text-muted text-end">
                      $
                      {segmentSensitivityData?.benchmark.spot_price.toLocaleString(
                        "en-US"
                      )}{" "}
                      pdpr
                    </td>
                  </tr>
                  {/* <div className="my-" /> */}
                  <tr>
                    <td className="text-muted">Fuel Price</td>
                    <td className="text-muted text-end">
                      $
                      {segmentSensitivityData?.benchmark.fuel_price.toLocaleString(
                        "en-US"
                      )}{" "}
                      mt/d
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Divider */}
            <div
              className="border border-gray d-flex align-items-stretch"
              style={{ width: "1px" }}
            />

            {/* Right part*/}
            <div className="w-100">
              <div className="text-muted text-center font-italic">
                User Input for calc
              </div>
              {/* Upper part */}
              <div className="d-flex justify-content-between flex-nowrap mt-3 align-items-center">
                <h3 style={{ color: "#ff5c29" }}>B</h3>
                <div>
                  <div style={{ color: "#ff5c29" }}>
                    {parseFloat(describedBallastSpeed)}
                  </div>
                  <div style={{ color: "#ff5c29" }}>
                    {parseFloat(ballastFuelCon)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-muted">kts</div>
                  <div className="text-muted">mt/day</div>
                </div>
                <div>
                  <div style={{ color: "#001446" }}>
                    {parseFloat(describedLadenSpeed)}
                  </div>
                  <div style={{ color: "#001446" }}>
                    {parseFloat(ladenFuelCon)}
                  </div>
                </div>
                <h3 style={{ color: "#001446" }}>L</h3>
              </div>
              <div className="mt-2 d-flex flex-column gap-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">Hire</div>
                  <div className="position-relative w-50 me-2">
                    <div className="PrevetSensitivity_DollarSign">$</div>
                    <Form.Control
                      className="bg-light w-100 text-end"
                      aria-label="Amount (to the nearest dollar)"
                      value={
                        hirePrice === "" || isNaN(Number(hirePrice))
                          ? ""
                          : Number(hirePrice).toLocaleString("en-US")
                      }
                      onFocus={(e) => {
                        updateChart({
                          isLaden: true,
                          speed: describedLadenSpeed,
                          isReset: false,
                        })
                        updateChart({
                          isLaden: false,
                          speed: describedBallastSpeed,
                          isReset: false,
                        })
                        updateVesselComparisonMessage()
                        setIsStandardVesselBoxGray(true)
                        setUserLadenVesselSpeed(describedLadenSpeed)
                        setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                        setIsSaveButtonHidden(false)
                      }}
                      onChange={(e) => {
                        if (e.target.value === "") {
                          setHirePrice("")
                        }
                        const regex = new RegExp(
                          /^(?:\d{1,3}(?:[,]\d{1,4})*|\d+)$/
                        )
                        const match = regex.test(e.target.value)
                        if (!match) {
                          return
                        }
                        const num = convertToLocaleNumStringToNumber(
                          e.target.value
                        ) // Convert 15,333 to 15333
                        setHirePrice(String(num))
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">Fuel</div>
                  <div className="position-relative w-50 me-2">
                    <div className="PrevetSensitivity_DollarSign">$</div>
                    <Form.Control
                      className="w-100 bg-light text-end"
                      aria-label="Amount (to the nearest dollar)"
                      value={fuelPrice}
                      onFocus={(e) => {
                        updateChart({
                          isLaden: true,
                          speed: describedLadenSpeed,
                          isReset: false,
                        })
                        updateChart({
                          isLaden: false,
                          speed: describedBallastSpeed,
                          isReset: false,
                        })
                        setUserLadenVesselSpeed(describedLadenSpeed)
                        setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                        updateVesselComparisonMessage()
                        setIsStandardVesselBoxGray(true)
                        setIsSaveButtonHidden(false)
                      }}
                      onChange={(e) => {
                        setFuelPrice(e.target.value)
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end w-100">
                  {isSaveButtonHidden ? null : (
                    <Button
                      variant="primary"
                      className="PrevetCards_SaveSensitivityButton d-flex align-items-center justify-content-center"
                      onClick={() =>
                        handleAddVesselSensitivityInput(
                          currentVsi as CreateVesselSensitivityInputObject
                        )
                      }
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          variant="light"
                        />
                      ) : (
                        <>
                          {" "}
                          <span className="ti-save font-weight-bold" />
                          <span className="font-weight-bold ms-2">Save</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function roundToNearestThousand(cost: number) {
  return Math.round(cost / 1000) * 1000
}

function DownIcon({ direction }: { direction: "up" | "down" }) {
  return (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 32 32"
      fill={`${direction === "down" ? "#e61f2f" : "#00763b"}`}
      className={`PrevetSensitivity_Icon--${direction}`}
    >
      <title>play3</title>
      <path d="M6 4l20 12-20 12z"></path>
    </svg>
  )
}

function convertToLocaleNumStringToNumber(numStr: string) {
  return Number(numStr.split(",").join(""))
}

export function calcPerformance({
  describedLadenSpeed,
  describedBallastSpeed,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  ladenFuelCon,
  ballastFuelCon,
  hirePrice,
  fuelPrice,
  ladenSeaDays,
  ballastSeaDays,
  isShowSeaMarginChecked,
  ladenAvgMargin,
  ballastAvgMargin,
}: {
  describedLadenSpeed: string
  describedBallastSpeed: string
  speedGraphLadenAvgSpeed?: number
  speedGraphBallastAvgSpeed?: number
  ladenFuelCon: string
  ballastFuelCon: string
  hirePrice: string
  fuelPrice: string
  ladenSeaDays: string
  ballastSeaDays: string
  isShowSeaMarginChecked: boolean
  ladenAvgMargin?: number
  ballastAvgMargin?: number
}) {
  let ladenCost
  let ballastCost

  if (speedGraphLadenAvgSpeed) {
    {
      isShowSeaMarginChecked &&
        ladenAvgMargin &&
        (speedGraphLadenAvgSpeed =
          speedGraphLadenAvgSpeed * (1 + ladenAvgMargin))
    }
    ladenCost =
      calcOverUnderPerformance({
        brokerSpeed: Number(describedLadenSpeed),
        actualSpeed: speedGraphLadenAvgSpeed,
        dailyFuelConsumption: Number(ladenFuelCon),
        dailyHireRate: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      }).costDeltaUSD * Number(ladenSeaDays)
  }

  if (speedGraphBallastAvgSpeed) {
    {
      isShowSeaMarginChecked &&
        ballastAvgMargin &&
        (speedGraphBallastAvgSpeed =
          speedGraphBallastAvgSpeed * (1 + ballastAvgMargin))
    }
    ballastCost =
      calcOverUnderPerformance({
        brokerSpeed: Number(describedBallastSpeed),
        actualSpeed: speedGraphBallastAvgSpeed,
        dailyFuelConsumption: Number(ballastFuelCon),
        dailyHireRate: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      }).costDeltaUSD * Number(ballastSeaDays)
  }

  if (ladenCost && ballastCost) {
    return roundToNearestThousand(ladenCost + ballastCost)
  }

  if (ladenCost) {
    return roundToNearestThousand(ladenCost)
  }
  if (ballastCost) {
    return roundToNearestThousand(ballastCost)
  }
}

export function getAvgSpeedFromPortStays({
  vesselStaysData,
  isLaden,
}: {
  vesselStaysData: VesselStay[] | undefined
  isLaden: boolean
}) {
  if (!vesselStaysData || vesselStaysData.length < 1) {
    return undefined
  }
  const portStaysData = getPortStaysData(vesselStaysData)
  const stays = isLaden ? portStaysData.laden : portStaysData.ballast

  if (!stays || stays.length < 1) {
    return undefined
  }

  return findAverage(stays.map((stay) => stay.avg_speed_mov))
}

export function useExtraCalculations(
  ballastSpeed: number,
  ballastFuelCon: number,
  ladenSpeed: number,
  ladenFuelCon: number,
  speedGraphLadenAvgSpeed: number | undefined,
  speedGraphBallastAvgSpeed: number | undefined,
  ballastSeaDays: number,
  ladenSeaDays: number,
  isShowSeaMarginChecked: boolean,
  ladenSmAvgMargin?: number,
  ballastSmAvgMargin?: number
) {
  const [extraTime, setExtraTime] = useState<number | null>(null)
  const [extraFuel, setExtraFuel] = useState<number | null>(null)

  useEffect(() => {
    if (
      (speedGraphBallastAvgSpeed &&
        ballastSpeed < speedGraphBallastAvgSpeed &&
        speedGraphLadenAvgSpeed &&
        ladenSpeed < speedGraphLadenAvgSpeed) ||
      ballastFuelCon <= 0 ||
      ladenFuelCon <= 0 ||
      !ballastSpeed ||
      !ladenSpeed ||
      !ladenFuelCon ||
      !ballastFuelCon ||
      !speedGraphLadenAvgSpeed ||
      !speedGraphBallastAvgSpeed
    ) {
      setExtraTime(null)
      setExtraFuel(null)
      return
    }

    if (isShowSeaMarginChecked) {
      if (ladenSmAvgMargin) {
        speedGraphLadenAvgSpeed =
          speedGraphLadenAvgSpeed! * (1 + ladenSmAvgMargin)
      }
      if (ballastSmAvgMargin) {
        speedGraphBallastAvgSpeed =
          speedGraphBallastAvgSpeed! * (1 + ballastSmAvgMargin)
      }
    }

    const extraTimeBallast = computeExtraTime(
      ballastSpeed,
      speedGraphBallastAvgSpeed,
      ballastSeaDays
    )
    const extraFuelBallast = extraTimeBallast * ballastFuelCon

    const extraTimeLaden = computeExtraTime(
      ladenSpeed,
      speedGraphLadenAvgSpeed,
      ladenSeaDays
    )
    const extraFuelLaden = extraTimeLaden * ladenFuelCon

    setExtraTime(extraTimeBallast + extraTimeLaden)
    setExtraFuel(extraFuelBallast + extraFuelLaden)
  }, [
    ballastSpeed,
    ladenSpeed,
    ballastFuelCon,
    ladenFuelCon,
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    ballastSeaDays,
    ladenSeaDays,
    isShowSeaMarginChecked
  ])

  return { extraTime, extraFuel }
}

export function computeExtraTime(speedCon: number, avgSpeed: number, seaDays: number) {
  const lagPerDay = speedCon * 24 - avgSpeed * 24
  const totalDistanceLagged = lagPerDay * seaDays
  const extraTime = totalDistanceLagged / avgSpeed / 24
  return extraTime
}

export function computePerformanceDifference({
  speedGraphBallastAvgSpeed,
  speedGraphLadenAvgSpeed,
  describedBallastSpeed,
  describedLadenSpeed,
  isShowSeaMarginChecked,
  ladenAvgMargin,
  ballastAvgMargin,
}: {
  speedGraphBallastAvgSpeed?: number
  speedGraphLadenAvgSpeed?: number
  describedBallastSpeed: string
  describedLadenSpeed: string
  isShowSeaMarginChecked: boolean
  ladenAvgMargin?: number
  ballastAvgMargin?: number
}) {
  if (speedGraphBallastAvgSpeed && speedGraphLadenAvgSpeed) {
    let speedGraphAvg = findAverage([
      speedGraphBallastAvgSpeed,
      speedGraphLadenAvgSpeed,
    ])
    {
      isShowSeaMarginChecked &&
        ladenAvgMargin &&
        ballastAvgMargin &&
        (speedGraphAvg =
          speedGraphAvg * (1 + findAverage([ladenAvgMargin, ballastAvgMargin])))
    }
    const describedAvg = findAverage([
      parseFloat(describedBallastSpeed),
      parseFloat(describedLadenSpeed),
    ])
    const diff = speedGraphAvg - describedAvg
    if (diff === 0) return " on par"
    const percentDiff = Math.round((diff / describedAvg) * 100)
    if (Math.abs(percentDiff) === 0) return " on par"
    if (diff < 0) {
      return (
        <span className="text-danger">{Math.abs(percentDiff)}% higher</span>
      )
    } else {
      return (
        <span className="text-success">{Math.abs(percentDiff)}% lower</span>
      )
    }
  }
}
